import React, { useState, useEffect } from 'react';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import { TrendingUp, Package, DollarSign } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency as formatCurrencyUtil } from '../../utils/currencyFormatter';

const PricingMarginCalculator = () => {
  const { user, profile } = useAuth();
  const [activeTab, setActiveTab] = useState('product-pricing');

  // Product Pricing State
  const [productData, setProductData] = useState({
    cost: '',
    desiredMargin: 25,
    targetPrice: 0,
    breakEvenPrice: 0,
    competitorPrice: '',
  });

  // Sale vs Margin State
  const [saleData, setSaleData] = useState({
    originalPrice: '',
    discountPercent: 0,
    salePrice: 0,
    originalMargin: 25,
    newMargin: 0,
    profitImpact: 0,
    minimumPrice: 0,
  });

  // Wholesale vs Retail State
  const [wholesaleData, setWholesaleData] = useState({
    cost: '',
    wholesaleVolume: 100,
    wholesalePrice: '',
    wholesaleMargin: 0,
    retailPrice: '',
    retailMargin: 0,
    volumeAdvantage: 0,
  });

  const parseNum = (val) => {
    const num = parseFloat(String(val)?.replace(/[^\d.-]/g, ''));
    return isNaN(num) ? 0 : num;
  };

  const formatCurrency = (amount) => {
    const userCurrency = profile?.currency || 'ZAR';
    return formatCurrencyUtil(parseNum(amount), userCurrency);
  };

  // Product Pricing Calculations
  const calculateProductPricing = () => {
    const cost = parseNum(productData?.cost);
    const marginPercent = parseNum(productData?.desiredMargin);
    
    if (cost <= 0) return;

    const targetPrice = cost / (1 - marginPercent / 100);
    const breakEvenPrice = cost;
    
    setProductData(prev => ({
      ...prev,
      targetPrice,
      breakEvenPrice,
    }));
  };

  // Sale vs Margin Calculations
  const calculateSaleImpact = () => {
    const originalPrice = parseNum(saleData?.originalPrice);
    const discountPercent = parseNum(saleData?.discountPercent);
    const originalMargin = parseNum(saleData?.originalMargin);
    
    if (originalPrice <= 0) return;

    const salePrice = originalPrice * (1 - discountPercent / 100);
    const cost = originalPrice * (1 - originalMargin / 100);
    const newMargin = ((salePrice - cost) / salePrice) * 100;
    const profitImpact = ((newMargin - originalMargin) / originalMargin) * 100;
    const minimumPrice = cost / (1 - (originalMargin * 0.5) / 100);
    
    setSaleData(prev => ({
      ...prev,
      salePrice,
      newMargin,
      profitImpact,
      minimumPrice,
    }));
  };

  // Wholesale vs Retail Calculations
  const calculateWholesaleRetail = () => {
    const cost = parseNum(wholesaleData?.cost);
    const wholesalePrice = parseNum(wholesaleData?.wholesalePrice);
    const retailPrice = parseNum(wholesaleData?.retailPrice);
    
    if (cost <= 0) return;

    const wholesaleMargin = ((wholesalePrice - cost) / wholesalePrice) * 100;
    const retailMargin = ((retailPrice - cost) / retailPrice) * 100;
    const volumeAdvantage = ((retailPrice - wholesalePrice) / wholesalePrice) * 100;
    
    setWholesaleData(prev => ({
      ...prev,
      wholesaleMargin,
      retailMargin,
      volumeAdvantage,
    }));
  };

  React.useEffect(() => {
    calculateProductPricing();
  }, [productData?.cost, productData?.desiredMargin]);

  React.useEffect(() => {
    calculateSaleImpact();
  }, [saleData?.originalPrice, saleData?.discountPercent, saleData?.originalMargin]);

  React.useEffect(() => {
    calculateWholesaleRetail();
  }, [wholesaleData?.cost, wholesaleData?.wholesalePrice, wholesaleData?.retailPrice]);

  const renderProductPricing = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-6">Product Configuration</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Product Cost</label>
            <input
              type="text"
              value={productData?.cost}
              onChange={(e) => setProductData(prev => ({ ...prev, cost: e?.target?.value }))}
              className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              placeholder="Enter product cost"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Desired Margin: {productData?.desiredMargin}%
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={productData?.desiredMargin}
              onChange={(e) => setProductData(prev => ({ ...prev, desiredMargin: e?.target?.value }))}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-slate-500 mt-1">
              <span>0%</span>
              <span>50%</span>
              <span>100%</span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Competitor Price (Optional)</label>
            <input
              type="text"
              value={productData?.competitorPrice}
              onChange={(e) => setProductData(prev => ({ ...prev, competitorPrice: e?.target?.value }))}
              className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              placeholder="Enter competitor price"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-6">Pricing Results</h3>
        <div className="space-y-4">
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <p className="text-sm text-green-900 mb-1">Target Price</p>
            <p className="text-3xl font-bold text-green-700">{formatCurrency(productData?.targetPrice)}</p>
            <p className="text-xs text-green-600 mt-1">With {productData?.desiredMargin}% margin</p>
          </div>
          <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
            <p className="text-sm text-orange-900 mb-1">Break-Even Price</p>
            <p className="text-2xl font-bold text-orange-700">{formatCurrency(productData?.breakEvenPrice)}</p>
            <p className="text-xs text-orange-600 mt-1">Minimum to cover costs</p>
          </div>
          {productData?.competitorPrice && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-900 mb-1">Competitive Positioning</p>
              <p className="text-lg font-bold text-blue-700">
                {parseNum(productData?.targetPrice) < parseNum(productData?.competitorPrice) 
                  ? 'Below competitor' :'Above competitor'}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Difference: {formatCurrency(Math.abs(parseNum(productData?.targetPrice) - parseNum(productData?.competitorPrice)))}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderSaleVsMargin = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-6">Sale Configuration</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Original Price</label>
            <input
              type="text"
              value={saleData?.originalPrice}
              onChange={(e) => setSaleData(prev => ({ ...prev, originalPrice: e?.target?.value }))}
              className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              placeholder="Enter original price"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Original Margin: {saleData?.originalMargin}%
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={saleData?.originalMargin}
              onChange={(e) => setSaleData(prev => ({ ...prev, originalMargin: e?.target?.value }))}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Discount: {saleData?.discountPercent}%
            </label>
            <input
              type="range"
              min="0"
              max="75"
              value={saleData?.discountPercent}
              onChange={(e) => setSaleData(prev => ({ ...prev, discountPercent: e?.target?.value }))}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-slate-500 mt-1">
              <span>0%</span>
              <span>37.5%</span>
              <span>75%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-6">Impact Analysis</h3>
        <div className="space-y-4">
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-900 mb-1">Sale Price</p>
            <p className="text-3xl font-bold text-blue-700">{formatCurrency(saleData?.salePrice)}</p>
            <p className="text-xs text-blue-600 mt-1">{saleData?.discountPercent}% discount applied</p>
          </div>
          <div className={`p-4 rounded-lg border ${
            saleData?.newMargin >= saleData?.originalMargin * 0.5 
              ? 'bg-green-50 border-green-200' :'bg-red-50 border-red-200'
          }`}>
            <p className="text-sm mb-1">New Margin</p>
            <p className="text-2xl font-bold">{saleData?.newMargin?.toFixed(2)}%</p>
            <p className="text-xs mt-1">
              {saleData?.profitImpact >= 0 ? '+' : ''}{saleData?.profitImpact?.toFixed(1)}% profit impact
            </p>
          </div>
          <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
            <p className="text-sm text-orange-900 mb-1">Minimum Safe Price</p>
            <p className="text-xl font-bold text-orange-700">{formatCurrency(saleData?.minimumPrice)}</p>
            <p className="text-xs text-orange-600 mt-1">To maintain profitability</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderWholesaleVsRetail = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-6">Pricing Configuration</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Product Cost</label>
            <input
              type="text"
              value={wholesaleData?.cost}
              onChange={(e) => setWholesaleData(prev => ({ ...prev, cost: e?.target?.value }))}
              className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              placeholder="Enter product cost"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Wholesale Price</label>
            <input
              type="text"
              value={wholesaleData?.wholesalePrice}
              onChange={(e) => setWholesaleData(prev => ({ ...prev, wholesalePrice: e?.target?.value }))}
              className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              placeholder="Enter wholesale price"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Retail Price</label>
            <input
              type="text"
              value={wholesaleData?.retailPrice}
              onChange={(e) => setWholesaleData(prev => ({ ...prev, retailPrice: e?.target?.value }))}
              className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              placeholder="Enter retail price"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Wholesale Volume: {wholesaleData?.wholesaleVolume} units
            </label>
            <input
              type="range"
              min="1"
              max="1000"
              value={wholesaleData?.wholesaleVolume}
              onChange={(e) => setWholesaleData(prev => ({ ...prev, wholesaleVolume: e?.target?.value }))}
              className="w-full"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-6">Profitability Analysis</h3>
        <div className="space-y-4">
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <p className="text-sm text-purple-900 mb-1">Wholesale Margin</p>
            <p className="text-3xl font-bold text-purple-700">{wholesaleData?.wholesaleMargin?.toFixed(2)}%</p>
            <p className="text-xs text-purple-600 mt-1">
              Volume: {wholesaleData?.wholesaleVolume} units
            </p>
          </div>
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <p className="text-sm text-green-900 mb-1">Retail Margin</p>
            <p className="text-3xl font-bold text-green-700">{wholesaleData?.retailMargin?.toFixed(2)}%</p>
            <p className="text-xs text-green-600 mt-1">Per unit basis</p>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-900 mb-1">Volume Advantage</p>
            <p className="text-2xl font-bold text-blue-700">{wholesaleData?.volumeAdvantage?.toFixed(1)}%</p>
            <p className="text-xs text-blue-600 mt-1">Retail premium over wholesale</p>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-sm text-slate-700 mb-2">Total Wholesale Revenue</p>
            <p className="text-xl font-bold text-slate-900">
              {formatCurrency(parseNum(wholesaleData?.wholesalePrice) * parseNum(wholesaleData?.wholesaleVolume))}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <Header />
      <main className="ml-64 mt-16 p-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Pricing & Margin Calculator</h1>
              <p className="text-sm text-slate-600">Analyze pricing strategies and optimize profit margins</p>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 mb-6">
          <div className="flex overflow-x-auto">
            <button
              onClick={() => setActiveTab('product-pricing')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-all border-b-2 ${
                activeTab === 'product-pricing' ?'border-blue-500 text-blue-700 bg-blue-50' :'border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Package className="w-4 h-4" />
                <span>Product Pricing</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('sale-vs-margin')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-all border-b-2 ${
                activeTab === 'sale-vs-margin' ?'border-blue-500 text-blue-700 bg-blue-50' :'border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <TrendingUp className="w-4 h-4" />
                <span>Sale vs Margin</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('wholesale-retail')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-all border-b-2 ${
                activeTab === 'wholesale-retail' ?'border-blue-500 text-blue-700 bg-blue-50' :'border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <DollarSign className="w-4 h-4" />
                <span>Wholesale vs Retail</span>
              </div>
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {activeTab === 'product-pricing' && renderProductPricing()}
          {activeTab === 'sale-vs-margin' && renderSaleVsMargin()}
          {activeTab === 'wholesale-retail' && renderWholesaleVsRetail()}
        </div>
      </main>
    </div>
  );
};

export default PricingMarginCalculator;